/**
 * pages/milestone-edit.tsx
 *
 * Edit an existing milestone.
 * - Title, date, note inputs (PATCH /milestones/:id)
 * - Current photo strip with × to remove (DELETE /milestones/:id/photos/:photoId)
 * - Add more photos (POST /milestones/:id/photos)
 * - Save navigates back to milestones list
 */
import { useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  useAddMilestonePhotos,
  useMilestones,
  useRemoveMilestonePhoto,
  useUpdateMilestone,
} from '../hooks/use-milestones'

interface PhotoPreview {
  id?: string // undefined = new photo not yet uploaded
  url: string
  file?: File
}

export default function MilestoneEditPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { data } = useMilestones()
  const milestone = data?.data.find((m) => m.id === id)

  const updateMilestone = useUpdateMilestone()
  const addPhotos = useAddMilestonePhotos()
  const removePhoto = useRemoveMilestonePhoto()

  const [title, setTitle] = useState(milestone?.title ?? '')
  const [date, setDate] = useState(milestone?.date ?? '')
  const [note, setNote] = useState(milestone?.note ?? '')
  const [photos, setPhotos] = useState<PhotoPreview[]>(
    milestone?.photo_urls?.map((url) => ({ url })) ?? [],
  )
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const fileInputRef = useRef<HTMLInputElement>(null)

  const canSave =
    title.trim().length > 0 && date.length > 0 && !saving && !updateMilestone.isPending

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(e.target.files ?? [])
    if (newFiles.length === 0) return
    const previews = newFiles.map((file) => ({
      url: URL.createObjectURL(file),
      file,
    }))
    setPhotos((prev) => [...prev, ...previews])
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleRemoveExisting = async (photoId: string, photoUrl: string) => {
    if (!id) return
    setPhotos((prev) => prev.filter((p) => p.url !== photoUrl))
    await removePhoto.mutateAsync({ milestoneId: id, photoId })
  }

  const handleRemoveNew = (url: string) => {
    setPhotos((prev) => {
      const removed = prev.find((p) => p.url === url)
      if (removed && !removed.id) URL.revokeObjectURL(removed.url)
      return prev.filter((p) => p.url !== url)
    })
  }

  const handleSave = async () => {
    if (!canSave || !id) return
    setError(null)
    setSaving(true)

    try {
      // Update text fields
      await updateMilestone.mutateAsync({
        id,
        input: {
          title: title.trim(),
          date,
          note: note.trim() || undefined,
        },
      })

      // Upload new photos
      const newPhotos = photos.filter((p) => !p.id && p.file)
      if (newPhotos.length > 0) {
        const formData = new FormData()
        for (const p of newPhotos) formData.append('files', p.file!)
        await addPhotos.mutateAsync({ id, formData })
      }

      navigate('/milestones')
    } catch (err) {
      setError((err as Error).message ?? 'Failed to save milestone')
      setSaving(false)
    }
  }

  if (!milestone) {
    return (
      <div className="min-h-screen bg-[#FAF8F4] flex flex-col max-w-[430px] mx-auto relative">
        <header className="pt-[env(safe-area-inset-top)] border-b border-[#F9E0E8]">
          <div className="flex items-center gap-3 px-5 h-[56px]">
            <button
              type="button"
              onClick={() => navigate('/milestones')}
              className="w-[36px] h-[36px] rounded-full bg-[#FBEAF0] flex items-center justify-center active:scale-90 transition-all duration-150 touch-manipulation"
              aria-label="Back"
            >
              <span className="text-[16px]">←</span>
            </button>
            <span className="font-['Playfair_Display'] text-[18px] font-medium text-[#2C1810]">
              Edit Milestone
            </span>
          </div>
        </header>
        <main className="flex-1 flex items-center justify-center">
          <p className="font-['DM_Sans'] text-[14px] text-[#B89AA2]">Milestone not found.</p>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#FAF8F4] flex flex-col max-w-[430px] mx-auto relative">
      {/* Sticky header */}
      <header className="sticky top-0 z-40 bg-[#FAF8F4] pt-[env(safe-area-inset-top)] border-b border-[#F9E0E8]">
        <div className="flex items-center justify-between px-5 h-[56px]">
          <button
            type="button"
            onClick={() => navigate('/milestones')}
            className="w-[36px] h-[36px] rounded-full bg-[#FBEAF0] flex items-center justify-center active:scale-90 transition-all duration-150 touch-manipulation"
            aria-label="Back"
          >
            <span className="text-[16px]">←</span>
          </button>
          <span className="font-['Playfair_Display'] text-[18px] font-medium text-[#2C1810]">
            Edit Milestone
          </span>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="h-[34px] px-4 rounded-full bg-[#D4537E] text-white font-['DM_Sans'] text-[14px] font-medium shadow-[0_4px_16px_rgba(212,83,126,0.28)] active:scale-95 active:bg-[#993556] transition-all duration-150 touch-manipulation disabled:opacity-40 disabled:shadow-none disabled:active:scale-100"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </header>

      {/* Form */}
      <main className="flex-1 overflow-y-auto px-5 pt-5 pb-10 flex flex-col gap-4 animate-fade-in-up">
        {/* Error banner */}
        {error && (
          <div className="bg-[#FFF0F4] border border-[#F4C0D1] rounded-[12px] px-4 py-3">
            <p className="font-['DM_Sans'] text-[13px] text-[#993556]">{error}</p>
          </div>
        )}

        {/* Title */}
        <div className="flex flex-col gap-1">
          <label
            className="font-['DM_Sans'] text-[12px] font-medium text-[#7A5A62] uppercase tracking-wide"
            htmlFor="edit-title"
          >
            Title
          </label>
          <input
            id="edit-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            className="w-full h-[48px] px-4 rounded-[12px] bg-white border border-[#F4C0D1] font-['DM_Sans'] text-[15px] text-[#2C1810] outline-none focus:border-[#D4537E] focus:ring-2 focus:ring-[#D4537E]/20 transition-all duration-150"
          />
        </div>

        {/* Date */}
        <div className="flex flex-col gap-1">
          <label
            className="font-['DM_Sans'] text-[12px] font-medium text-[#7A5A62] uppercase tracking-wide"
            htmlFor="edit-date"
          >
            Date
          </label>
          <input
            id="edit-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full h-[48px] px-4 rounded-[12px] bg-white border border-[#F4C0D1] font-['DM_Sans'] text-[15px] text-[#2C1810] outline-none focus:border-[#D4537E] focus:ring-2 focus:ring-[#D4537E]/20 transition-all duration-150"
          />
        </div>

        {/* Note */}
        <div className="flex flex-col gap-1">
          <label
            className="font-['DM_Sans'] text-[12px] font-medium text-[#7A5A62] uppercase tracking-wide"
            htmlFor="edit-note"
          >
            Note <span className="font-normal normal-case text-[#B89AA2]">(optional)</span>
          </label>
          <textarea
            id="edit-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={1000}
            rows={4}
            className="w-full px-4 py-3 rounded-[12px] bg-white border border-[#F4C0D1] font-['DM_Sans'] text-[15px] text-[#2C1810] outline-none focus:border-[#D4537E] focus:ring-2 focus:ring-[#D4537E]/20 transition-all duration-150 resize-none leading-relaxed"
          />
        </div>

        {/* Photos */}
        <div className="flex flex-col gap-2">
          <label
            className="font-['DM_Sans'] text-[12px] font-medium text-[#7A5A62] uppercase tracking-wide"
            htmlFor="edit-photos"
          >
            Photos
          </label>

          {photos.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {photos.map((photo, i) => {
                const photoId = photo.id
                return (
                  <div
                    key={photo.url}
                    className="relative rounded-[12px] overflow-hidden border border-[#F4C0D1] aspect-square bg-[#FAF8F4]"
                  >
                    <img
                      src={photo.url}
                      alt={`Moment ${i + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        photoId
                          ? handleRemoveExisting(photoId, photo.url)
                          : handleRemoveNew(photo.url)
                      }
                      disabled={removePhoto.isPending}
                      className="
                        absolute top-1 right-1
                        w-[24px] h-[24px] rounded-full
                        bg-black/50 flex items-center justify-center
                        text-white text-[14px]
                        active:scale-90 transition-all duration-150
                        touch-manipulation disabled:opacity-40
                      "
                      aria-label={`Remove moment ${i + 1}`}
                    >
                      ×
                    </button>
                  </div>
                )
              })}
            </div>
          )}

          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="
              w-full h-[48px] rounded-[12px]
              bg-white border-2 border-dashed border-[#F4C0D1]
              font-['DM_Sans'] text-[14px] text-[#B89AA2]
              flex items-center justify-center gap-2
              active:bg-[#FBEAF0] transition-all duration-150
              touch-manipulation
            "
          >
            <span>📷</span>
            <span>Add more moments</span>
          </button>

          <input
            id="edit-photos"
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handlePhotoChange}
            className="hidden"
          />
        </div>
      </main>
    </div>
  )
}
